import { type ActionsDuJour, actionsDuJour, type FamilleAction } from '@imp/shared/actions-du-jour';
import { messageErreur } from '@imp/shared/message-erreur';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CalendarDays,
  CheckCheck,
  FileText,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { type AlertesResult, computeAlertes } from '@/modules/alertes/api';
import { useAuth } from '@/modules/auth/AuthContext';
import { formatEuro } from '@/modules/catalogue/api';
import { type ChiffresTableauBord, chargerDashboard, type Echeance } from '@/modules/dashboard/api';
import { Entonnoir, GraphiqueImpayes, GraphiqueMois } from '@/modules/dashboard/charts';
import {
  listSuiviFacturation,
  type LigneFacturationAffichee,
} from '@/modules/pilotage/facturation-api';
import { CarteTachesDuJour } from '@/modules/taches/CarteTachesDuJour';

/** Le pourcentage d'écart d'un mois sur l'autre. Null quand il n'y a rien à comparer. */
function evolution(actuel: number, precedent: number): number | null {
  if (precedent <= 0) return null;
  return (actuel - precedent) / precedent;
}

/**
 * Ce qu'une échéance dit d'elle-même sous sa date : l'heure d'une intervention,
 * l'objet d'une facture. La mise en forme de l'heure regarde la langue de
 * l'écran — c'est pour cela qu'elle vit ici, et pas dans le calcul.
 */
function detailEcheance(e: Echeance): string {
  if (e.nature === 'facture') return e.objet ?? 'Facture à encaisser';
  if (e.debutAt === null) return 'Journée entière';
  return new Date(e.debutAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

interface Tuile {
  label: string;
  valeur: string;
  precision?: string | undefined;
  delta?: number | null | undefined;
  teinte?: string | undefined;
  cadre?: string | undefined;
  lien?: string | undefined;
}

/**
 * Une tuile de chiffre. Le chiffre prend la couleur de ce qu'il dit : rouge
 * s'il alerte, vert si c'est de l'argent qui doit rentrer. L'évolution porte
 * sa flèche — jamais la couleur seule.
 */
function TuileChiffre({ tuile }: { tuile: Tuile }) {
  const contenu = (
    <>
      <span className="text-muted-foreground text-[13px] font-semibold uppercase tracking-[0.04em]">
        {tuile.label}
      </span>
      <div>
        <span
          className={`block text-[28px] font-bold tabular-nums leading-8 tracking-tight sm:text-[34px] sm:leading-10 ${tuile.teinte ?? ''}`}
        >
          {tuile.valeur}
        </span>
        {tuile.delta !== null && tuile.delta !== undefined ? (
          <span className="text-muted-foreground mt-0.5 flex items-center gap-1 text-xs font-medium">
            {tuile.delta >= 0 ? (
              <TrendingUp className="h-3.5 w-3.5" />
            ) : (
              <TrendingDown className="h-3.5 w-3.5" />
            )}
            {tuile.delta >= 0 ? '+' : ''}
            {Math.round(tuile.delta * 100)} % vs mois dernier
          </span>
        ) : tuile.precision !== undefined ? (
          <span className="text-muted-foreground mt-0.5 block text-xs">{tuile.precision}</span>
        ) : null}
      </div>
    </>
  );
  const classe = `flex min-h-[104px] flex-col justify-between gap-2 p-4 sm:min-h-[116px] sm:p-5 ${tuile.cadre ?? ''}`;
  return tuile.lien !== undefined ? (
    <Card className={`${classe} hover:bg-muted/40`}>
      <Link to={tuile.lien} className="contents">
        {contenu}
      </Link>
    </Card>
  ) : (
    <Card className={classe}>{contenu}</Card>
  );
}

/**
 * Où mène une ligne du bloc.
 *
 * Vers la liste filtrée quand son filtre dit EXACTEMENT la même chose que
 * l'alerte — sinon le tableau de bord dirait 4 et la liste 6. Les factures en
 * brouillon sont toutes à émettre (un avoir naît émis, jamais brouillon) ; les
 * devis « à envoyer » sont tous à envoyer. Pour le reste, l'alerte est plus
 * précise qu'un statut : une facture émise en retard est à encaisser, pas à
 * envoyer ; un devis signé déjà facturé n'est plus à convertir. Ces familles
 * mènent au centre d'alertes, filtré.
 */
const LISTE_EXACTE: Partial<Record<FamilleAction, string>> = {
  factures_a_emettre: '/factures?statut=brouillon',
  devis_a_envoyer: '/devis?statut=a_envoyer',
};

/**
 * « À faire aujourd'hui » : cinq lignes, chacune une action, chacune un lien.
 *
 * Remplace la ligne « 103 choses à traiter, dont 41 prioritaires » : un chiffre
 * de 103 ne se lit pas, une liste de cinq actions se traite. Chaque ligne ouvre
 * le centre d'alertes déjà filtré sur cette action.
 */
function BlocActionsDuJour({
  actions,
  chargement,
  voitLesChiffres,
}: {
  actions: ActionsDuJour | null;
  chargement: boolean;
  /** Les montants sont réservés à la direction, comme les tuiles d'argent. */
  voitLesChiffres: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
        <CardTitle className="text-lg">À faire aujourd&apos;hui</CardTitle>
        {actions !== null && actions.total > 0 ? (
          <Link to="/alertes" className="text-primary shrink-0 text-sm hover:underline">
            Tout voir · {actions.total} alerte{actions.total > 1 ? 's' : ''}
          </Link>
        ) : null}
      </CardHeader>
      <CardContent className="pt-0">
        {chargement ? (
          <p className="text-muted-foreground py-2 text-sm">Analyse des dossiers…</p>
        ) : actions === null ? (
          // La lecture a échoué : le bandeau du haut le dit. Pas de « tout est à
          // jour » ici, ce serait faux.
          <p className="text-muted-foreground py-2 text-sm">
            Impossible de dire ce qui attend pour le moment.
          </p>
        ) : actions.lignes.length === 0 ? (
          <p className="flex items-center gap-3 py-2">
            <CheckCheck className="text-money-in h-5 w-5 shrink-0" />
            <span className="text-base font-semibold">Tout est à jour : rien n&apos;attend.</span>
          </p>
        ) : (
          <ul className="divide-y">
            {actions.lignes.map((l) => (
              <li key={l.famille}>
                <Link
                  to={LISTE_EXACTE[l.famille] ?? `/alertes?famille=${l.famille}`}
                  className="hover:bg-muted/40 -mx-2 flex min-h-14 items-center justify-between gap-3 rounded-md px-2 py-2"
                >
                  <span className="min-w-0">
                    <span className="text-base">
                      <span
                        className={`font-bold tabular-nums ${l.urgents > 0 ? 'text-prio-high' : ''}`}
                      >
                        {l.nombre}
                      </span>{' '}
                      {l.nom}
                    </span>
                    {l.precision !== null || (voitLesChiffres && l.montant !== null) ? (
                      <span className="text-muted-foreground block text-sm">
                        {l.precision !== null ? (
                          // La composition (« 1 en retard · 2 soldes partiels »)
                          // peut ne rien porter d'urgent : rouge seulement s'il y en a.
                          <span className={l.urgents > 0 ? 'text-prio-high' : undefined}>
                            {l.precision}
                          </span>
                        ) : null}
                        {l.precision !== null && voitLesChiffres && l.montant !== null
                          ? ' · '
                          : null}
                        {voitLesChiffres && l.montant !== null ? (
                          // HT ou TTC, dit à chaque fois : les chantiers à facturer
                          // se chiffrent en HT, le reste en TTC.
                          <span className="tabular-nums">
                            {formatEuro(l.montant)}
                            {l.base !== null ? ` ${l.base}` : ''}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </span>
                  <ArrowRight className="text-muted-foreground h-5 w-5 shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
        {actions !== null && actions.famillesMasquees > 0 ? (
          <Link
            to="/alertes"
            className="text-muted-foreground mt-2 block text-sm hover:underline"
          >
            + {actions.famillesMasquees} autre{actions.famillesMasquees > 1 ? 's' : ''} sujet
            {actions.famillesMasquees > 1 ? 's' : ''} moins pressant
            {actions.famillesMasquees > 1 ? 's' : ''} — tout voir
          </Link>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function Dashboard() {
  const { profil } = useAuth();
  // Le chiffre d'affaires, la trésorerie et les impayés sont réservés à la
  // direction (RLS côté base) : l'écran d'un chef de chantier montre son
  // travail, pas les comptes de l'entreprise.
  const voitLesChiffres = profil?.isAdmin ?? false;

  const alertesQ = useQuery<AlertesResult>({ queryKey: ['alertes'], queryFn: computeAlertes });
  const chiffresQ = useQuery<ChiffresTableauBord>({
    queryKey: ['dashboard-chiffres'],
    queryFn: () => chargerDashboard(),
  });
  // Lecture lourde (cinq tables) : elle ne part que pour qui peut la voir, et
  // seulement une fois le reste de l'écran déjà affiché.
  const aFacturerQ = useQuery<LigneFacturationAffichee[]>({
    queryKey: ['dashboard-a-facturer'],
    queryFn: listSuiviFacturation,
    enabled: voitLesChiffres,
  });

  const counts = alertesQ.data?.counts ?? { haute: 0, moyenne: 0, basse: 0, total: 0 };
  const c = chiffresQ.data;
  const aFacturer = (aFacturerQ.data ?? [])
    .filter((l) => l.motif === 'intervention_passee' && (l.resteAFacturerHt ?? 0) > 0)
    .sort((a, b) => (b.joursDepuis ?? 0) - (a.joursDepuis ?? 0))
    .slice(0, 5);

  const echeances = (c?.echeances ?? [])
    .filter((e) => voitLesChiffres || e.nature === 'agenda')
    .slice(0, 6);

  const tuilesArgent: Tuile[] = [
    {
      label: 'CA facturé ce mois',
      valeur: formatEuro(c?.caMoisHt ?? 0),
      teinte: 'text-money-in',
      delta: c === undefined ? undefined : evolution(c.caMoisHt, c.caMoisPrecedentHt),
    },
    {
      label: 'Encours client',
      valeur: formatEuro(c?.encours ?? 0),
      precision:
        (c?.encoursRetardLong ?? 0) > 0
          ? `dont ${formatEuro(c?.encoursRetardLong ?? 0)} à + de 30 j`
          : 'rien au-delà de 30 jours',
      teinte: (c?.encoursRetardLong ?? 0) > 0 ? 'text-prio-high' : '',
      lien: '/factures',
    },
    {
      label: 'Devis en attente',
      valeur: String(c?.devisEnAttente ?? 0),
      precision: `${formatEuro(c?.devisEnAttenteMontantHt ?? 0)} HT en jeu`,
      lien: '/devis',
    },
    {
      label: 'Transformation 3 mois',
      valeur:
        c?.tauxTransformation === null || c?.tauxTransformation === undefined
          ? '—'
          : `${String(Math.round(c.tauxTransformation * 100))} %`,
      precision: 'devis signés / envoyés',
    },
  ];

  const tuilesTerrain: Tuile[] = [
    { label: 'Chantiers actifs', valeur: String(c?.chantiersActifs ?? 0), lien: '/chantiers' },
    {
      label: 'Cette semaine',
      valeur: String((c?.echeances ?? []).filter((e) => e.nature === 'agenda').length),
      precision: 'interventions prévues',
      lien: '/agenda',
    },
    {
      label: 'Alertes prioritaires',
      valeur: String(counts.haute),
      teinte: counts.haute > 0 ? 'text-prio-high' : '',
      lien: '/alertes',
    },
    { label: 'À traiter', valeur: String(counts.total), lien: '/alertes' },
  ];

  const tuiles = voitLesChiffres ? [...tuilesArgent, ...tuilesTerrain] : tuilesTerrain;

  const aujourdhui = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Tableau de bord</h1>
        <p className="text-muted-foreground">
          Bonjour {profil?.prenom ?? ''} — {aujourdhui}
        </p>
      </div>

      {/* D'abord ce qu'il y a à faire, ensuite les chiffres : les tuiles de suivi
          passent dessous. */}
      <BlocActionsDuJour
        actions={alertesQ.data ? actionsDuJour(alertesQ.data.alertes) : null}
        chargement={alertesQ.isLoading}
        voitLesChiffres={voitLesChiffres}
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {tuiles.map((t) => (
          <TuileChiffre key={t.label} tuile={t} />
        ))}
      </div>

      {chiffresQ.isError ? (
        <Card>
          <CardContent className="text-prio-high py-4 text-sm">
            Les chiffres n&apos;ont pas pu être chargés : {messageErreur(chiffresQ.error)}
          </CardContent>
        </Card>
      ) : null}

      <CarteTachesDuJour />

      {voitLesChiffres ? (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Activité mensuelle</CardTitle>
              <Button asChild variant="outline" size="sm">
                <Link to="/pilotage">
                  Pilotage <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {c === undefined ? (
                <p className="text-muted-foreground text-sm">Calcul…</p>
              ) : (
                <GraphiqueMois points={c.mois} />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Impayés par ancienneté</CardTitle>
              </CardHeader>
              <CardContent>
                {c === undefined ? (
                  <p className="text-muted-foreground text-sm">Calcul…</p>
                ) : c.encours === 0 ? (
                  <div className="text-muted-foreground flex items-center gap-2 py-4 text-sm">
                    <CheckCheck className="text-money-in h-5 w-5" /> Aucun impayé.
                  </div>
                ) : (
                  <GraphiqueImpayes tranches={c.impayes} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Devis, 3 derniers mois</CardTitle>
              </CardHeader>
              <CardContent>
                {c === undefined ? (
                  <p className="text-muted-foreground text-sm">Calcul…</p>
                ) : (c.entonnoir[0]?.nombre ?? 0) === 0 ? (
                  <p className="text-muted-foreground py-4 text-sm">
                    Aucun devis envoyé sur la période.
                  </p>
                ) : (
                  <Entonnoir etapes={c.entonnoir} />
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Les 7 prochains jours</CardTitle>
            <Button asChild variant="outline" size="sm">
              <Link to="/agenda">
                Agenda <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {c === undefined ? (
              <p className="text-muted-foreground text-sm">Chargement…</p>
            ) : echeances.length === 0 ? (
              <p className="text-muted-foreground py-4 text-sm">Rien de prévu cette semaine.</p>
            ) : (
              <div className="divide-y">
                {echeances.map((e) => (
                  <Link
                    key={e.id}
                    to={e.lien}
                    className="hover:bg-muted/40 flex min-h-14 items-center justify-between gap-3 py-3"
                  >
                    <div className="flex items-center gap-3">
                      {e.nature === 'agenda' ? (
                        <CalendarDays className="text-prio-low h-5 w-5 shrink-0" />
                      ) : (
                        <FileText className="text-money-in h-5 w-5 shrink-0" />
                      )}
                      <div>
                        <div className="text-base font-semibold">{e.titre}</div>
                        <div className="text-muted-foreground text-sm">
                          {new Date(`${e.date}T12:00:00`).toLocaleDateString('fr-FR', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                          })}{' '}
                          — {detailEcheance(e)}
                        </div>
                      </div>
                    </div>
                    {e.montant !== null ? (
                      <span className="text-money-in shrink-0 text-base font-semibold tabular-nums">
                        {formatEuro(e.montant)}
                      </span>
                    ) : null}
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {voitLesChiffres ? (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>À facturer</CardTitle>
              <Button asChild variant="outline" size="sm">
                <Link to="/pilotage">
                  Suivi <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {aFacturerQ.isLoading ? (
                <p className="text-muted-foreground text-sm">Analyse…</p>
              ) : aFacturer.length === 0 ? (
                <div className="text-muted-foreground flex items-center gap-2 py-4 text-sm">
                  <CheckCheck className="text-money-in h-5 w-5" /> Rien n&apos;attend de facture.
                </div>
              ) : (
                <div className="divide-y">
                  {aFacturer.map((l) => (
                    <Link
                      key={l.chantierId}
                      to={`/chantiers/${l.chantierId}`}
                      className="hover:bg-muted/40 flex min-h-14 items-center justify-between gap-3 py-3"
                    >
                      <div>
                        <div className="text-base font-semibold">{l.libelle}</div>
                        <div className="text-muted-foreground text-sm">
                          {l.client}
                          {l.joursDepuis !== null
                            ? ` — terminé il y a ${String(l.joursDepuis)} j`
                            : ''}
                        </div>
                      </div>
                      <span className="text-money-in shrink-0 text-base font-semibold tabular-nums">
                        {formatEuro(l.resteAFacturerHt ?? 0)}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
